/**
 * @module UsersLists
 */

import { User } from "./user.model";
import { USERSLISTS_SVC, UsersListsService } from "rainbow-web-sdk/services/users/usersLists.service";
import { MainService } from "rainbow-web-sdk/services";


export interface UsersListProfile {
    /**
     * Name of the users list
     */
    name?: string;

    /**
     * A description of the purpose of the users list
     */
    description?: string;

    /**
     * Data indicating whether the list has been marked as favorite or not.
     */
    isFavorite?: boolean;
}


export interface UsersList {
    /**
     * Unique identifier of the users list
     * @readonly
     */
    id: string;

    /**
     * Name of the users list
     */
    name: string;

    /**
     * Description of the users list
     */
    description: string;

    /**
     * Data indicating whether the list has been marked as favorite or not.
     */
    isFavorite: boolean;

    /**
     * User(s) members of the list
     * @readonly
     */
    users: User[];

    /**
     * Add a user in the list.
     * A ON_USER_ADDED_TO_LIST event is sent afterwards at the completion of the operation to notify about adding the user to the list.
     * @param user - The user to add to users list
     */
    addUser(user: User): Promise<any>;

    /**
     * Remove a user from the list.
     * A ON_USER_REMOVED_FROM_LIST event is sent afterwards at the completion of the operation to notify about removing a user from the list.
     * @param user - Reference of the user to remove from the list
     */
    removeUser(user: User): Promise<any>;


    /**
     * Update the profile of the users list.
     * Only the general information, i.e. the name, the description and the favorite status can be updated using this method.
     * Users can only be added or removed from the list using methods 'addUser' and 'removeUser' respectively.
     * A ON_USERS_LIST_PROFILE_UPDATED event is sent afterwards at the completion of the operation to notify about the list's profile update.
     * @param profile - The profile information to be updated
     */
    updateProfile(profile: UsersListProfile): Promise<any>;

}


/**
 * @internal
 */
export class UsersListRB implements UsersList {

    /**
     * The unique identifier of the users list
     * @readonly
     */
    public id: string;

    /**
     * The name of the users list
     */
    public name: string;

    /**
     * The filtername of the users list (in order to operate some pattern finding with name)
     * UCaaS Only: Internal management
     */
    public filterName?: string = null; //RQRAINB-11637

    /**
     * A description of the purpose of the list
     */
    public description: string = "";

    /**
     * Data indicating whether the list has been marked as favorite or not.
     */
    public isFavorite: boolean = false;

    /**
     * The users who are members of the list
     */
    public users: User[] = [];

    private usersListsService?: any;

    constructor(id: string, name: string, description: string = "", isFavorite: boolean = false) {
        this.usersListsService = MainService.getInstance().inject<UsersListsService>(USERSLISTS_SVC);

        this.id = id;
        this.name = name;
        this.description = description;
        this.isFavorite = isFavorite;
    }

    public static createFromData(data) {
        return new UsersListRB(data.id, data.name, data.comment, data.isFavorite);
    }

    /**
     * Add a user in the list.
     * A ON_USER_ADDED_TO_LIST event is sent afterwards at the completion of the operation to notify about adding the user to the list.
     * @param user - The user to add to users list
     */
    public async addUser(user: User): Promise<void> {
        this.usersListsService?.addUserToList(user, this);
    }

    /**
     * Remove a user from the list.
     * A ON_USER_REMOVED_FROM_LIST event is sent afterwards at the completion of the operation to notify about removing a user from the list.
     * @param user - Reference of the user to remove from the list
     */
    public async removeUser(user: User): Promise<void> {
        this.usersListsService?.removeUserFromList(user, this);
    }


    /**
     * Update the profile of the users list.
     * Only the general information, i.e. the name, the description and the favorite status can be updated using this method.
     * Users can only be added or removed from the list using methods 'addUser' and 'removeUser' respectively.
     * A ON_USERS_LIST_PROFILE_UPDATED event is sent afterwards at the completion of the operation to notify about the list's profile update.
     * @param profile - The profile information to be updated
     */
    public updateProfile(profile: UsersListProfile): Promise<any> {
        let listUpdated: boolean = false;

        if (profile?.name && (profile.name !== this.name)) {
            const newName: string = profile.name.trim();
            if (newName.length !== 0) {
                listUpdated = true;
                this.name = profile.name;
            }
        }

        if (profile?.description && (profile.description !== this.description)) {
            listUpdated = true;
            this.description = profile.description;
        }

        if (profile?.isFavorite && (profile.isFavorite !== this.isFavorite)) {
            listUpdated = true;
            this.isFavorite = profile.isFavorite;
        }

        if (!listUpdated) return;

        this.usersListsService?.updateUsersListInfo(this);
    }
}
